import logger from '@/logger'

// Mirrors the server-side `ReplicatedSiteRewriteFormat` (web2 appsettings: "/pws/{1}/{0}").
// {0} = original path (leading slash trimmed), {1} = replicated-site business name.
const REPLICATED_SITE_REWRITE_FORMAT = '/pws/{1}/{0}'

export class UrlHelpers {
  /**
   * Re-applies the replicated-site path rewrite to an ABSOLUTE settings URL so the link
   * stays on the consultant's replicated site (e.g. .../pws/jennyshop/...).
   *
   * Most AOE links are Umbraco content links that get rewritten server-side via
   * `ToReplicatedSiteUrl`. TermsUrl / PrivacyPolicyUrl are free-text absolute URLs that
   * skip that path, so we rewrite them here on the client.
   *
   *   https://www.pamperedchef.at/rechtliches/agb  +  "jennyshop"
   *   -> https://www.pamperedchef.at/pws/jennyshop/rechtliches/agb
   *
   * Origin, query string and hash are preserved. Returns the input unchanged when there is
   * no business name, when parsing fails, or when the path is already prefixed.
   */
  static toReplicatedSiteUrl(absoluteUrl: string | null | undefined, businessName: string | null | undefined): string {
    if (!absoluteUrl) return absoluteUrl ?? ''
    if (!businessName) return absoluteUrl

    try {
      const url = new URL(absoluteUrl)
      const prefix = `/pws/${businessName}`

      // Guard against double-prefixing if the setting already includes the replicated path.
      if (url.pathname === prefix || url.pathname.startsWith(`${prefix}/`)) {
        return url.href
      }

      const trimmedPath = url.pathname.replace(/^\/+/, '')
      url.pathname = REPLICATED_SITE_REWRITE_FORMAT
        .replace('{1}', businessName)
        .replace('{0}', trimmedPath)

      logger.debug('toReplicatedSiteUrl', { absoluteUrl, businessName, result: url.href })
      return url.href
    } catch (error) {
      logger.warn('toReplicatedSiteUrl invalid URL', { absoluteUrl, businessName, error })
      return absoluteUrl
    }
  }

  static getSafeReturnUrl(returnUrl: string | null, trustedUrls: string[] = []): string | null {
    if (!returnUrl) {
      logger.debug('getSafeReturnUrl', { returnUrl, result: null, reason: 'empty returnUrl' })
      return null
    }

    if (typeof window === 'undefined') {
      logger.debug('getSafeReturnUrl', { returnUrl, result: null, reason: 'window unavailable' })
      return null
    }

    try {
      const url = new URL(returnUrl, window.location.origin)
      const allowedOrigins = new Set([window.location.origin])

      for (const trustedUrl of trustedUrls) {
        if (trustedUrl) {
          const trustedOrigin = new URL(trustedUrl, window.location.origin).origin
          allowedOrigins.add(trustedOrigin)
        }
      }

      if ((url.protocol === 'http:' || url.protocol === 'https:') && allowedOrigins.has(url.origin)) {
        logger.debug('getSafeReturnUrl', { returnUrl, result: url.href, allowedOrigins: Array.from(allowedOrigins) })
        return url.href
      }

      logger.warn('getSafeReturnUrl blocked URL', {
        returnUrl,
        protocol: url.protocol,
        origin: url.origin,
        allowedOrigins: Array.from(allowedOrigins),
      })
    } catch (error) {
      logger.warn('getSafeReturnUrl invalid URL', { returnUrl, error })
      return null
    }

    return null
  }

  /**
   * Validates a configurable link (e.g. one supplied through an override message) before it is
   * opened. Only http/https URLs are allowed, so values such as `javascript:` or `data:` are
   * rejected. Relative URLs are resolved against the current origin.
   */
  static getSafeLinkUrl(linkUrl: string | null | undefined): string | null {
    if (!linkUrl || !linkUrl.trim()) return null
    if (typeof window === 'undefined') return null

    try {
      const url = new URL(linkUrl.trim(), window.location.origin)
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        return url.href
      }

      logger.warn('getSafeLinkUrl blocked URL', { linkUrl, protocol: url.protocol })
    } catch (error) {
      logger.warn('getSafeLinkUrl invalid URL', { linkUrl, error })
    }

    return null
  }
}
